
import React from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, LinkIcon } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import NTBIntegration from '../interview/NTBIntegration';
import ContextArticleDisplay from '../interview/ContextArticleDisplay';
import HashtagGenerationButton from './HashtagGenerationButton';

interface ContentSectionProps {
  context: string;
  setContext: (value: string) => void;
  contextUrl: string;
  setContextUrl: (value: string) => void;
  contextImageUrl: string;
  setContextImageUrl: (value: string) => void;
  contextImageCaption: string;
  setContextImageCaption: (value: string) => void;
  question: string;
  setQuestion: (value: string) => void;
  answer: string;
  setAnswer: (value: string) => void;
  tags: string[];
  setTags: (value: string[]) => void;
  validTags: string[];
}

const ContentSection: React.FC<ContentSectionProps> = ({
  context,
  setContext,
  contextUrl,
  setContextUrl,
  contextImageUrl,
  setContextImageUrl,
  contextImageCaption,
  setContextImageCaption,
  question,
  setQuestion,
  answer,
  setAnswer,
  tags,
  setTags,
  validTags
}) => {
  const { t } = useTranslation();
  
  // Helper function to check if URL is NTB article
  const isNTBArticle = (url: string) => {
    return url && url.startsWith('ntb:');
  };

  return (
    <div className="space-y-4">
      {/* NTB Integration */}
      <NTBIntegration 
        onArticleSelect={(article) => {
          setContext(article.context);
          setContextUrl(article.contextUrl);
          if (article.contextImageUrl) {
            setContextImageUrl(article.contextImageUrl);
          }
          if (article.contextImageCaption) {
            setContextImageCaption(article.contextImageCaption);
          }
        }}
        className="mb-4"
      />

      {/* Display context article if available */}
      {context && (
        <ContextArticleDisplay
          context={context}
          contextUrl={contextUrl}
          isNTBArticle={isNTBArticle(contextUrl)}
          className="mb-4"
        />
      )}

      <div>
        <div className="flex items-center gap-2 mb-1">
          <label htmlFor="contextUrl" className="block text-sm font-medium text-gray-700">
            Kontekst URL for ekstern artikkel (valgfri)
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-help">
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-80">
                <p>Legg til en ekstern URL. Hvis du vil bruke NTB, bruk NTB-integrasjonen over.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="relative">
          <input
            id="contextUrl"
            type="url"
            value={contextUrl}
            onChange={(e) => {
              setContextUrl(e.target.value);
              // If manual URL is added and not NTB, set context
              if (e.target.value && !e.target.value.startsWith('ntb:')) {
                setContext(`Ekstern artikkel: ${e.target.value}`);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md pr-8"
            placeholder="https://example.com/article"
          />
          {contextUrl && (
            <LinkIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-5 w-5 text-blue-500" />
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Hvis du legger til en URL, vil konteksten være klikkbar i artikkelen og åpne den eksterne siden.
        </p>
      </div>
      
      <div>
        <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.question')} *
        </label>
        <textarea
          id="question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          required
        />
      </div>
      
      <div>
        <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-1">
          {t('production.form.answer')} *
        </label>
        <textarea
          id="answer"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          rows={6}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          required
        />
        <p className="mt-2 text-sm text-gray-500">
          {t('production.form.wordCount')}: {answer.trim() ? answer.trim().split(/\s+/).length : 0}
        </p>
      </div>
      
      <div>
        <div className="flex items-center justify-between mb-1">
          <label htmlFor="tags" className="block text-sm font-medium text-gray-700">
            {t('production.form.tags')}
          </label>
          <HashtagGenerationButton
            question={question}
            answer={answer}
            onHashtagsGenerated={setTags}
          />
        </div>
        <input
          id="tags"
          type="text"
          value={tags.map(tag => tag.startsWith('#') ? tag : `#${tag}`).join(', ')}
          onChange={(e) => setTags(e.target.value.split(',').map(tag => tag.trim().replace(/^#/, '')).filter(tag => tag))}
          className="w-full px-3 py-2 border border-gray-300 rounded-md"
          placeholder="#frontend, #backend, #leadership"
        />
        <p className="mt-1 text-sm text-gray-500">
          {t('production.form.validTags')}: {validTags.join(', ')}
        </p>
      </div>
    </div>
  );
};

export default ContentSection;
